/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { User } from '@jda/lui-common-icon-library-mui5';
import { isIFrame } from '@jda/lui-portal-utilities';
import { AppBar, Avatar, Box, Divider, IconButton, Menu, MenuItem, Switch, Toolbar, Typography } from '@mui/material';

import { useStyles } from './styles';
import { AuthService } from '../../auth/authService';
import { changeThemeAction } from '../../store/theme/themeActionCreator';

export const CustomAppBar = (props: any) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    const themeFromLocalStorage = localStorage.getItem('theme');
    if (themeFromLocalStorage) setIsDarkTheme(themeFromLocalStorage === 'dark' ? true : false);
    else setIsDarkTheme(false);
    dispatch(changeThemeAction(themeFromLocalStorage === 'dark' ? 'dark' : 'light'));
  }, []);

  const handleChangeTheme = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      localStorage.setItem('theme', 'dark');
      dispatch(changeThemeAction('dark'))
    } else {
      localStorage.setItem('theme', 'light');
      dispatch(changeThemeAction('light'));
    }
    setIsDarkTheme(event.target.checked);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onSignOut = async () => {
    await AuthService.getInstance().logout();
    setAnchorEl(null);
  };

  const getIdTokenClaims = () => {
    return props?.accountInfo?.idTokenClaims;
  };

  const getIdTokenClaimValue = (key: string) => {
    // @ts-ignore
    return (getIdTokenClaims() || {})[key];
  };

  const getUsername = () => {
    return props?.accountInfo?.preferred_username;
  };

  const getFallbackAvatar = (fontSize?: string) => {
    const firstName = props?.accountInfo?.given_name;
    const secondName = props?.accountInfo?.family_name;
    const firstLetter = firstName ? firstName.charAt(0) : '';
    const secondLetter = secondName ? secondName.charAt(0) : '';
    const avatarTxt = [firstLetter, secondLetter].join('');
    return avatarTxt !== '' ? (
      avatarTxt
    ) : (
      // @ts-ignore
      <User fontSize={fontSize} data-testid="avatar-fallback-user-icon" />
    );
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const iframeClass = isIFrame() ? classes.isIframe : "";
  return (
    <Box maxHeight={500} className={classes.root}>
      <Box minWidth="100%" display="flex" justifyContent="center" alignItems="flex-start" className={classes.componentHeaderWrap}>
        <AppBar position="fixed" color="transparent" className={classes.componentHeaderWrap} >
          <Toolbar variant="dense" className={iframeClass}>
            <Box flexGrow="1">
              <Typography className={`${classes.componentHeader} ${iframeClass}`}>
                Management Console
              </Typography>
            </Box>
            {!isIFrame() &&
              <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleClick}
                  color="inherit"
                >
                  <Avatar alt={getUsername()} src={getIdTokenClaimValue('picture')} className="app-bar-fallback-avatar">
                    {getFallbackAvatar('small')}
                  </Avatar>
                </IconButton>
                <Menu
                  open={open}
                  id="account-menu"
                  anchorEl={anchorEl}
                  onClose={handleClose}
                  // onClick={handleClose}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <MenuItem>
                    <Avatar alt={getUsername()} src={getIdTokenClaimValue('picture')} className="app-bar-fallback-avatar">
                      {getFallbackAvatar('small')}
                    </Avatar>
                    <Typography variant="body1" color="textPrimary" className={classes.menuItemText}>
                      {props?.accountInfo?.preferred_username || props?.accountInfo?.given_name + ' ' + props?.accountInfo?.family_name}
                    </Typography>
                  </MenuItem>
                  <MenuItem className={classes.darkThemeWrap}>
                    <Typography variant="body1" color="textPrimary">
                      {'Dark Theme'}
                    </Typography>
                    <Switch
                      checked={isDarkTheme}
                      onChange={handleChangeTheme}
                    />
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={onSignOut}>
                    Sign Out
                  </MenuItem>
                </Menu>
              </Box>
            }
          </Toolbar>
        </AppBar>
      </Box>
    </Box>
  );
};